import Header from "@/components/header";
import PersianSupportNumberInput from "@/components/persianNumberInput";
import { MerchantServicePriceType } from "@/models/club";
import { useGetMerchantServices } from "@/services/appointment/hooks/useGetMerchantServices";
import useUpdateMerchantService from "@/services/appointment/hooks/useUpdateMerchantService";
import {
  Button,
  Card,
  Container,
  Grid,
  NativeSelect,
  Stack,
  Text,
  Textarea,
  TextInput,
  Title,
} from "@mantine/core";
import { useForm, yupResolver } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import * as Yup from "yup";
import "./addService.style.scss";
import { times, UpdateMerchantServiceCommandForm } from "./type";
import { useDisclosure } from "@mantine/hooks";
import SelectCategory from "./SelectCategory";
import useIsSmall from "@/hooks/useIsSmall";

const schema = Yup.object().shape({
  title: Yup.string().required("لطفا نام سرویس را وارد نمایید"),
  durationPartInMinute: Yup.string().required(
    "لطفا بازه زمانی نوبت دهی را انتخاب نمایید"
  ),
  serviceCategoryId: Yup.number().required("دسته بندی را انتخاب نمایید"),
  startingPrice: Yup.number().when("priceType", {
    is: (priceType: number) => {
      return Number(priceType) === MerchantServicePriceType.Value1;
    },
    then: (schema) => {
      return schema.required("قیمت را وارد نمایید");
    },
    otherwise: (schema) => {
      return schema;
    },
  }),
});

function EditScheduleServices() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { state } = useLocation();

  const [searchParams] = useSearchParams();

  const { mutate, isLoading } = useUpdateMerchantService();

  const { data: categories } = useGetMerchantServices();

  const [selectedCategory, setSelectedCategory] = useState("");
  const isSmall = useIsSmall();
  const [openedCategory, { open: openCategory, close: closeCategory }] =
    useDisclosure(false);

  const form = useForm<UpdateMerchantServiceCommandForm>({
    validate: yupResolver(schema),
  });

  const submit = (values: UpdateMerchantServiceCommandForm) => {
    mutate(
      {
        id: values?.id,
        durationPartInMinute: values?.durationPartInMinute
          ? Number(values?.durationPartInMinute)
          : undefined,
        color: values?.color,
        title: values?.title,
        description: values?.description,
        priceType: Number(values?.priceType),
        startingPrice: values?.startingPrice,
        serviceCategoryId: values?.serviceCategoryId,
      },
      {
        onSuccess() {
          notifications.show({
            title: "عملیات موفق",
            message: "عملیات با موفقیت صورت پذیرفت",
            color: "green",
          });
          queryClient.invalidateQueries({
            queryKey: ["getMerchantServices"],
          });
          queryClient.refetchQueries(["getMerchantServices"]).then(() => {
            navigate("/dashboard/schedule/services");
          });
        },
      }
    );
  };
  const { setValues } = form;

  useEffect(() => {
    const service = state?.service;
    const categoryId = state?.categoryId;
    setSelectedCategory(categoryId);
    if (state?.service) {
      setValues({
        id: service?.id,
        serviceCategoryId: Number(categoryId),
        title: state?.service?.title,
        description: state?.service?.description,
        durationPartInMinute: service?.durationPartInMinute,
        priceType: service?.priceType,
        startingPrice: service?.startingPrice,
        color: service?.color,
      });
    }
  }, []);

  useEffect(() => {
    const catId = searchParams.get("catId");
    if (catId) {
      setSelectedCategory(catId);
    }
  }, []);

  useEffect(() => {
    if (selectedCategory) {
      setValues({
        serviceCategoryId: Number(selectedCategory),
      });
    }
  }, [setValues, selectedCategory]);

  const selectedCategoryItem = categories?.find(
    (x) => x.id === form?.values?.serviceCategoryId
  );

  return (
    <>
      <Header
        title="ویرایش خدمت"
        showBack
        route="/dashboard/schedule/services"
      />
      <Container mb="100px" w="100%" size="md" bg="white">
        <Title order={4}>ویرایش خدمت</Title>
        <Stack mt="md" justify="start" flex={1}>
          <form
            onSubmit={form.onSubmit(submit)}
            name="add-service"
            className="w-100"
            id="add-service"
          >
            <Stack>
              <TextInput
                styles={{
                  label: {
                    fontSize: "1rem",
                  },
                }}
                size="lg"
                placeholder=""
                withAsterisk
                description="به عنوان مثال : کوتاه کردن مو"
                label="عنوان"
                {...form.getInputProps("title")}
              />

              <Textarea
                styles={{
                  label: {
                    fontSize: "1rem",
                  },
                }}
                size="lg"
                label="توضیحات"
                {...form.getInputProps("description")}
              />

              <TextInput
                size="lg"
                onClick={openCategory}
                error={form?.errors?.serviceCategoryId}
                label="دسته بندی"
                readOnly
                value={selectedCategoryItem?.title || ""}
                rightSection={
                  <Text c="blue" size="xs" onClick={openCategory}>
                    انتخاب
                  </Text>
                }
              />

              <NativeSelect
                styles={{
                  label: {
                    fontSize: "1rem",
                  },
                }}
                size="lg"
                withAsterisk
                label="بازه زمانی نوبت دهی"
                description="هر چند دقیقه یک بار نوبت ثبت شود"
                {...form.getInputProps("durationPartInMinute")}
                onChange={(e) => {
                  if (e)
                    setValues({
                      durationPartInMinute: Number(e.target.value),
                    });
                }}
                data={times}
              />

              <Grid>
                <Grid.Col span={6}>
                  <NativeSelect
                    {...form.getInputProps("priceType")}
                    styles={{
                      label: {
                        fontSize: "1rem",
                      },
                    }}
                    size="lg"
                    label="نوع قیمت"
                    data={[
                      {
                        label: "ثابت",
                        value: MerchantServicePriceType.Value0.toString(),
                      },
                      {
                        label: "قیمت از",
                        value: MerchantServicePriceType.Value1.toString(),
                      },
                      {
                        label: "رایگان",
                        value: MerchantServicePriceType.Value2.toString(),
                      },
                    ]}
                  />
                </Grid.Col>
                <Grid.Col span={6}>
                  <PersianSupportNumberInput
                    size="lg"
                    required={
                      Number(form?.values?.priceType) ===
                      MerchantServicePriceType.Value1
                    }
                    withAsterisk={
                      Number(form?.values?.priceType) ===
                      MerchantServicePriceType.Value1
                    }
                    withThousandsSeparator
                    value={form?.values?.startingPrice?.toString() || ""}
                    leftSection={<Text>ریال</Text>}
                    styles={{
                      input: {
                        direction: "ltr",
                        textAlign: "left",
                        paddingLeft: "1rem",
                      },
                      label: {
                        fontSize: "1rem",
                      },
                    }}
                    label="قیمت"
                    onChangeHandler={(e) =>
                      form.setValues({
                        startingPrice: Number(e),
                      })
                    }
                    error={form?.errors?.startingPrice}
                  />
                </Grid.Col>
              </Grid>

              {/* <NativeSelect
                size="md"
                label="ظرفیت نوبت دهی"
                description="محدودیت تعداد نوبت در هر بازه زمانی ، به عنوان مثال : در هر 20 دقیقه این تعداد نوبت دهی مجاز می باشد"
                {...form.getInputProps("capacity")}
                onChange={(e) => {
                  if (e)
                    setValues({
                      capacity: e.target.value,
                    });
                }}
                data={[
                  {
                    label: "",
                    value: "",
                  },
                  ...(Array.from({ length: 50 }, (_, i) => ({
                    label: (i + 1).toString(),
                    value: (i + 1).toString(),
                  })) || []),
                ]}
              /> */}

              {/* <Stack gap="5">
                <Group gap="xs">
                  <Text>رنگ سرویس</Text>
                  <IconCircleFilled
                    size="15"
                    color={form?.values?.color || ""}
                  />
                </Group>

                <ColorPicker
                  format="hex"
                  {...form.getInputProps("color")}
                  withPicker={false}
                  fullWidth
                  styles={{
                    swatches: {
                      gap: "5px",
                    },
                    swatch: {
                      "--cs-radius": "100px",
                    },
                  }}
                  swatches={[
                    ...DEFAULT_THEME.colors.red.slice(3, 4),
                    ...DEFAULT_THEME.colors.green.slice(3, 4),
                    ...DEFAULT_THEME.colors.blue.slice(3, 4),
                    ...DEFAULT_THEME.colors.yellow.slice(3, 4),
                    ...DEFAULT_THEME.colors.gray.slice(3, 4),
                    ...DEFAULT_THEME.colors.lime.slice(3, 4),
                    ...DEFAULT_THEME.colors.pink.slice(3, 4),
                    ...DEFAULT_THEME.colors.violet.slice(3, 4),
                    ...DEFAULT_THEME.colors.orange.slice(3, 4),
                    ...DEFAULT_THEME.colors.cyan.slice(3, 4),
                    ...DEFAULT_THEME.colors.indigo.slice(3, 4),
                  ]}
                />
              </Stack> */}
            </Stack>
          </form>
        </Stack>
      </Container>
      {isSmall ? (
        <Card
          styles={{
            root: {
              position: "fixed",
              bottom: 0,
              left: 0,
              right: 0,
              zIndex: 10,
            },
          }}
          withBorder
          radius={0}
        >
          <Button
            size="lg"
            fullWidth
            type="submit"

            form="add-service"
            loading={isLoading}
          >
            ویرایش
          </Button>
        </Card>
      ) : (
        <Container w="100%" size="md" bg="white">
          <Button
            size="lg"
            fullWidth
            type="submit"

            form="add-service"
            loading={isLoading}
          >
            ویرایش
          </Button>
        </Container>
      )}

      <SelectCategory
        openedCategory={openedCategory}
        closeCategory={closeCategory}
        value={selectedCategory}
        setValue={setSelectedCategory}
        categories={categories || []}
      />
    </>
  );
}

export default EditScheduleServices;
